import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// library to encrypt user id (same scheme as authSaga / SSOLoginSuccess)
import SHA512 from 'crypto-js/sha512';
import HMACSHA512 from 'crypto-js/hmac-sha512';
import Base64 from 'crypto-js/enc-base64';
import Cookies from 'universal-cookie';
// core functions
import ls from 'lib/core/storageFactory';
import { setBaseUrl } from 'api';

const cookies = new Cookies();

/**
 * Entra web SSO takeover page.
 *
 * The backend Entra callback (full-page redirect flow) lands the browser here,
 * ON the client's own subdomain, with the auth result in the query string:
 *
 *   https://<client>.<domain>/authenticate?token=..&client=..&email=..&username=..&fresh_support=..
 *
 * We mirror what redux-utils/sagas/authSaga.js `userLoginSaga` does on a normal
 * password login (so the app boots identically), scrub the token out of the URL,
 * then hand off to /app. On any missing/malformed params we bounce back to the
 * apex /login with an error so the user is never stranded on a blank page.
 *
 * This is Entra-only; the legacy SAML page (pages/Auth/SSOLoginSuccess.js) and
 * the Google popup flow are untouched.
 */

const COOKIE_OPTS = {
  path: '/',
  domain: `.${process.env.REACT_APP_SHELF_REDIRECT_URI}`,
  sameSite: 'none',
  secure: 'true'
};

// [ENTRA-SSO-DEBUG] temporary rollout logging helpers — grep the tag to remove.
/* eslint-disable no-console */
const dbg = (...args) => console.log('[ENTRA-SSO-DEBUG][authenticate]', ...args);
const dbgErr = (...args) =>
  console.error('[ENTRA-SSO-DEBUG][authenticate]', ...args);
const maskToken = t => (t ? `<len=${t.length} prefix=${t.slice(0, 6)}...>` : '<none>');
/* eslint-enable no-console */

const failBackToLogin = (reason = 'invalid_session') => {
  const base =
    process.env.REACT_APP_SHELF_DEPLOYMENT_ENV === 'local'
      ? `http://${process.env.REACT_APP_SHELF_REDIRECT_URI}`
      : `https://${process.env.REACT_APP_SHELF_REDIRECT_URI}`;
  const target = `${base}/login?sso_error=${encodeURIComponent(reason)}`;
  dbg('failing back to login:', target);
  window.location.replace(target);
};

const Authenticate = () => {
  const location = useLocation();

  useEffect(() => {
    dbg('page mounted. raw search =', location.search);
    try {
      const params = new URLSearchParams(location.search);
      const token = params.get('token');
      const client = params.get('client');
      const email = params.get('email') || '';
      const username = params.get('username') || '';
      // Backend serialises a Python bool into the URL as "True"/"False";
      // normalise back to a real boolean so authProfile matches the shape
      // password login stores.
      const freshSupport = ['True', 'true', '1'].includes(
        params.get('fresh_support')
      );

      dbg('parsed params:', {
        token: maskToken(token),
        client,
        email,
        username,
        fresh_support_raw: params.get('fresh_support'),
        freshSupport,
        sso_error: params.get('sso_error')
      });

      // A backend failure should already have redirected to /login; guard anyway.
      if (params.get('sso_error')) {
        dbg('sso_error present in params, bouncing to login:', params.get('sso_error'));
        failBackToLogin(params.get('sso_error'));
        return;
      }
      if (!token || !client) {
        dbgErr('missing token or client -> invalid_session', {
          hasToken: !!token,
          hasClient: !!client
        });
        failBackToLogin('invalid_session');
        return;
      }

      const baseURL = `https://${client}.${process.env.REACT_APP_SHELF_API_REDIRECT_URI}`;
      dbg('computed baseURL =', baseURL);

      // Populate local session state — mirrors authSaga.userLoginSaga.
      ls.set('authToken', token);
      ls.set('client', client);
      ls.set('domain', client);
      ls.set('baseURL', baseURL);
      setBaseUrl(baseURL);
      ls.set('isLoggedIn', true);
      ls.set('firstLogin', true);
      // SSO users have no password to reset.
      ls.set('showResetPassword', false);
      ls.set('authProfile', {
        email,
        token,
        status: 200,
        fresh_support: freshSupport,
        username
      });
      dbg('ls populated (authToken/client/domain/baseURL/isLoggedIn/authProfile set)');

      // gtag user id + traffic type cookies (same derivation as authSaga).
      const trafficType = username.includes('@infilect') ? 'internal' : 'external';
      cookies.set('trafficType', trafficType, COOKIE_OPTS);
      const hashDigest = SHA512(username);
      const encryptedUserId = Base64.stringify(
        HMACSHA512(hashDigest, process.env.REACT_APP_SHELF_GTAG_ENCKEY)
      );
      cookies.set('userId', encryptedUserId, COOKIE_OPTS);
      dbg('cookies set (trafficType=' + trafficType + ', userId)');

      // Sanity-check that the shared .infiviz.ai cookies are actually readable
      // (this is what the subdomain app will rely on).
      dbg('readback isLoggedIn=', ls.get('isLoggedIn'), 'authToken=', maskToken(ls.get('authToken')));

      // Scrub the token out of the URL / history / referer before moving on.
      window.history.replaceState({}, '', '/authenticate');
      dbg('URL scrubbed; redirecting to /app on', window.location.hostname);

      // Already on the correct subdomain, so a relative hop is enough.
      window.location.replace('/app');
    } catch (e) {
      dbgErr('exception while handling authenticate params:', e);
      failBackToLogin('invalid_session');
    }
  }, [location.search]);

  return null;
};

export default Authenticate;
